// Meal API Service - City Collab Pattern
// Handles meal data fetching and management operations

import { get, post } from "@/services/axiosUitils";

// Type definitions
interface FoodItem {
  name: string;
  serving_size_g: number;
  serving_description: string;
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
  meal?: string;
  meal_id?: string;
  food_item_id?: string;
  id?: number;
  image_url?: string;
  created_on?: string;
  created_at?: string;
  updated_at?: string;
  timestamp?: string;
  uploaded_at?: string;
  date_added?: string;
  [key: string]: any;
}

interface MealTotals {
  carbohydrate_content_g: number;
  dietary_fiber_g: number;
  net_carbs_g: number;
  sugar_g: number;
  protein_content_g: number;
  fat_content_g: number;
  trans_fat_g: number;
  saturated_fat_g: number;
  calorie_content_kcal: number;
  GI_estimate: number;
  GL_estimate: number;
}

interface MealData {
  document_id?: string;
  id?: string;
  meal_time?: string;
  meal_name?: string;
  meal_items?: FoodItem[];
  food_items?: FoodItem[]; // Alias for meal_items
  meal_totals?: MealTotals;
  totals?: MealTotals; // Alias for meal_totals
  meal_image?: string;
  image_url?: string; // Alias for meal_image
  date?: string;
  [key: string]: any;
}

interface MealDataResponse {
  success: boolean;
  data: MealData[];
  date: string;
  date_local?: string;
  message?: string;
}

interface FoodItemsResponse {
  results: FoodItem[];
  count: number;
  page?: number;
  page_size?: number;
  next?: string | null;
  previous?: string | null;
}

interface SaveMealDataRequest {
  document_id: string;
  date: string;
  meal_type: string;
  food_items: FoodItem[];
  totals: MealTotals;
}

interface SaveMealDataResponse {
  success: boolean;
  message?: string;
  data?: any;
}

interface MealHistoryDay {
  date: string;
  total_meals: number;
  kcal: number;
  protein: number;
  fat: number;
  carbs: number;
  fiber: number;
}

interface MealHistoryResponse {
  links: {
    next: string | null;
    previous: string | null;
  };
  count: number;
  page: number;
  page_size: number;
  results: MealHistoryDay[];
}

// API Endpoints Configuration
const ENDPOINTS = {
  getMealData: (date: string) => `/food_scanner/meal_data/get/?date=${date}`,
  getFoodItems: "/food_scanner/food-items/",
  saveMealData: "/food_scanner/meal_data/save/",
  getMealHistory: (month: number, year: number) =>
    `/food_scanner/get_meal_history/?month=${month}&year=${year}`,
  // Alternative endpoints to try
  getMealDataAlt: (date: string) => `/food_scanner/meals/?date=${date}`,
  getMealDataAlt2: (date: string) => `/food_scanner/meal_data/?date=${date}`,
} as const;

/**
 * MealApiService - Manages meal data and food items
 * Uses City Collab pattern with common axios utilities
 */
class MealApiService {
  /**
   * Fetch meal data for a specific date
   * @param dateString - Date in YYYY-MM-DD format
   * @returns Meal data for the specified date
   */
  async getMealDataByDate(dateString: string): Promise<any> {
    try {
      const endpoint = ENDPOINTS.getMealData(dateString);
      const response = await get<any>(endpoint);
      return response;
    } catch (error: any) {
      // Try alternative endpoints if main one fails
      try {
        const altEndpoint = ENDPOINTS.getMealDataAlt(dateString);
        const altResponse = await get<any>(altEndpoint);
        return altResponse;
      } catch (_altError: any) {
        // Alternative endpoint also failed, continue to throw original error
      }

      throw new Error(
        error.response?.data?.message ??
        "Failed to load meal data. Please try again."
      );
    }
  }

  /**
   * Fetch all food items
   * @returns All food items with pagination info
   */
  async getAllFoodItems(): Promise<FoodItemsResponse> {
    try {
      const response = await get<FoodItemsResponse>(ENDPOINTS.getFoodItems);
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ??
        "Failed to load food items. Please try again."
      );
    }
  }

  /**
   * Fetch food items filtered by meal ID
   * @param mealId - The meal ID to filter by
   * @returns Food items belonging to the specified meal
   */
  async getFoodItemsByMealId(mealId: string): Promise<FoodItem[]> {
    try {
      const response = await this.getAllFoodItems();
      const filteredItems = response.results.filter(
        item => item.meal === mealId
      );
      return filteredItems;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ??
        "Failed to load meal items. Please try again."
      );
    }
  }

  /**
   * Save meal data with food items
   * @param mealData - Meal data object containing document_id, date, meal_type, food_items, and totals
   * @returns Response from the server
   */
  async saveMealData(
    mealData: SaveMealDataRequest
  ): Promise<SaveMealDataResponse> {
    try {
      const response = await post<SaveMealDataResponse>(
        ENDPOINTS.saveMealData,
        mealData
      );
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ??
        "Failed to save meal data. Please try again."
      );
    }
  }

  /**
   * Fetch meal history for a specific month and year
   * @param month - Month number (1-12)
   * @param year - Year (e.g., 2025)
   * @returns Meal history data grouped by date
   */
  async getMealHistory(
    month: number,
    year: number
  ): Promise<MealHistoryResponse> {
    try {
      const endpoint = ENDPOINTS.getMealHistory(month, year);
      const response = await get<MealHistoryResponse>(endpoint);
      return response;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ??
        "Failed to load meal history. Please try again."
      );
    }
  }
}

// Export singleton instance
const mealApiService = new MealApiService();
export default mealApiService;

// Export types for use in components
export type {
  FoodItem,
  MealTotals,
  MealData,
  MealDataResponse,
  FoodItemsResponse,
  SaveMealDataRequest,
  SaveMealDataResponse,
  MealHistoryDay,
  MealHistoryResponse,
};
